import Parse from 'parse';

// This app's Parse server.
export const FORMS_APP_ID = 'myAppId';
export const FORMS_SERVER_URL = 'https://forms.bethelchristiancenter.com/api/parse';

// Pastor's Tools reads from a DIFFERENT Parse server with a different app id.
const BETHEL_APP_ID = 'bethel';
const BETHEL_SERVER_URL = 'https://app.bethelchristiancenter.com/parse';

Parse.initialize(FORMS_APP_ID);
Parse.serverURL = FORMS_SERVER_URL;

// The permanent global administrator. Mirrors SUPER_ADMIN_EMAIL in
// cloud/admin-management.js; used here to gate "view as".
export const SUPER_ADMIN_EMAIL = 'troe@bethelchristianschools.com';

/**
 * Read-only mode — the enforcement behind "view as another user".
 *
 * Impersonation is a display concept (the app renders as the target user), but
 * the real Parse session is still the admin's, so nothing stops that session
 * from writing. This is the backstop that does: while read-only is on, every
 * mutating Parse call is refused. It's an ALLOWLIST of reads (fail safe — a new
 * cloud function is blocked until someone deliberately lists it), plus a hard
 * block on every object save/destroy. So an admin viewing as someone can never
 * forge an approval or change that person's data, even by accident.
 */
let readOnly = false;
export const setReadOnly = (value) => { readOnly = !!value; };
export const isReadOnly = () => readOnly;

// Cloud functions that only read. Everything else is treated as a write.
const READ_ONLY_CLOUD_FUNCTIONS = new Set([
    'getMyAdminStatus',
    'getUserRoles',
    'getFormById',
    'getFormApprovalStatus',
    'getPendingFormsForRole',
    'getApprovedFormsByRole',
    'getDeniedFormsByRole',
    'getCancelledFormsByRole',
    'listMembers',
    'generatePDF',            // renders/returns a PDF to view; no user data changes
    'exportJobApplicationPDF',
    'getWebsiteContent',
    'listWebsiteContactMessages',
]);

const blocked = (what) =>
    Promise.reject(new Error(`You're viewing as another user (read-only), so ${what} is blocked. Exit to make changes.`));

const _cloudRun = Parse.Cloud.run.bind(Parse.Cloud);
Parse.Cloud.run = (name, params, options) => {
    if (readOnly && !READ_ONLY_CLOUD_FUNCTIONS.has(name)) return blocked(`"${name}"`);
    return _cloudRun(name, params, options);
};

const _save = Parse.Object.prototype.save;
Parse.Object.prototype.save = function (...args) {
    if (readOnly) return blocked('saving');
    return _save.apply(this, args);
};
const _destroy = Parse.Object.prototype.destroy;
Parse.Object.prototype.destroy = function (...args) {
    if (readOnly) return blocked('deleting');
    return _destroy.apply(this, args);
};
const _fileSave = Parse.File.prototype.save;
Parse.File.prototype.save = function (...args) {
    if (readOnly) return blocked('uploading');
    return _fileSave.apply(this, args);
};

// Note on reaching the member app (app.bethelchristiancenter.com): we never
// repoint this global SDK at it. That singleton switch used to sign people out
// intermittently, because the rest of the app kept querying the forms server on
// the same singleton while it was pointed away. Instead, reads go through the
// direct-fetch helpers below, and writes go through the forms server (see
// cloud/pastors-tools.js).

/**
 * Read from the Bethel app's Parse server WITHOUT touching the global SDK.
 *
 * The switch-the-singleton approach (switchToBethelApp/withBethelApp) is unsafe
 * in this app: the shell always keeps a Forms query in flight (the approval
 * badge), so repointing the shared SDK across an await intermittently sends the
 * wrong request to the wrong server. These helpers talk to Bethel over plain
 * fetch — no shared state — and decode the REST JSON back into real Parse
 * objects with Parse.Object.fromJSON, so callers keep using .get()/.id/.url()
 * exactly as they did with the SDK.
 *
 * Reads work with only the app-id header. Writes on the Bethel server require a
 * Bethel-app session, which the Forms app doesn't have — those still go through
 * the SDK switch for now.
 */
const bethelHeaders = () => ({
    'X-Parse-Application-Id': BETHEL_APP_ID,
    'Content-Type': 'application/json',
});

// A cloud function can return Parse objects (as {__type:'Object'}), dates, or
// plain values; turn the rich ones back into the SDK types callers expect.
const decodeBethel = (value) => {
    if (Array.isArray(value)) return value.map(decodeBethel);
    if (value && typeof value === 'object') {
        if (value.__type === 'Object' || value.__type === 'Pointer') {
            return Parse.Object.fromJSON(value);
        }
        if (value.__type === 'Date') return new Date(value.iso);
    }
    return value;
};

export const bethelRun = async (functionName, params = {}) => {
    const response = await fetch(`${BETHEL_SERVER_URL}/functions/${functionName}`, {
        method: 'POST',
        headers: bethelHeaders(),
        body: JSON.stringify(params),
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || `Bethel server returned ${response.status}`);
    return decodeBethel(body.result);
};

export const bethelQuery = async (className, { where, include, order, limit } = {}) => {
    const params = new URLSearchParams();
    if (where) params.set('where', JSON.stringify(where));
    if (include) params.set('include', include);
    if (order) params.set('order', order);
    if (limit != null) params.set('limit', String(limit));

    const response = await fetch(`${BETHEL_SERVER_URL}/classes/${className}?${params.toString()}`, {
        headers: bethelHeaders(),
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || `Bethel server returned ${response.status}`);
    // Each result is decoded with the class name it belongs to, which also
    // rebuilds any included pointers and File/Date fields inside it.
    return (body.results || []).map((result) => Parse.Object.fromJSON({ className, ...result }));
};

// A Date the Bethel REST API will accept inside a `where` clause.
export const bethelDate = (value) => ({ __type: 'Date', iso: new Date(value).toISOString() });

/**
 * The real, working URL for a file stored on the app server.
 *
 * The app's Parse server has a misconfigured public URL, so the url() it hands
 * back is literally prefixed with the string "undefined"
 * (e.g. "undefined/files/bethel/xyz.pdf"). The stored file NAME is correct,
 * though, and the file is served publicly, so we rebuild the URL ourselves.
 * Fixing it here means it keeps working, and starts working off url() with no
 * change, if the app server is ever corrected.
 */
export const bethelFileUrl = (file) => {
    if (!file) return null;
    const name = typeof file.name === 'function' ? file.name() : file.name;
    if (!name) return null;
    return `${BETHEL_SERVER_URL}/files/${BETHEL_APP_ID}/${encodeURIComponent(name)}`;
};

// Keep the existing API endpoints for non-form functionality
const BASE_URL = 'http://localhost:3001';

export const API = {
    accounts: {
        list: () => `${BASE_URL}/accounts`,
        create: () => `${BASE_URL}/accounts`,
        update: (id) => `${BASE_URL}/accounts/${id}`,
        delete: (id) => `${BASE_URL}/accounts/${id}`,
    },
    vendors: {
        list: () => `${BASE_URL}/vendors`,
        create: () => `${BASE_URL}/vendors`,
        update: (id) => `${BASE_URL}/vendors/${id}`,
        delete: (id) => `${BASE_URL}/vendors/${id}`,
        uploadW9: (id) => `${BASE_URL}/vendors/${id}/documents/w9`,
        uploadInsurance: (id) => `${BASE_URL}/vendors/${id}/documents/insurance`,
    },
    purchaseOrders: {
        list: () => `${BASE_URL}/purchase-orders`,
        create: () => `${BASE_URL}/purchase-orders`,
        update: (id) => `${BASE_URL}/purchase-orders/${id}`,
        delete: (id) => `${BASE_URL}/purchase-orders/${id}`,
        approve: (id) => `${BASE_URL}/purchase-orders/${id}/approve`,
        uploadLineItemAttachment: (poId, lineItemId) =>
            `${BASE_URL}/purchase-orders/${poId}/line-items/${lineItemId}/attachment`,
    }
};

// Define Parse SDK classes for forms
export const ParseClasses = {
    Form: 'Form',
    CampusLockUp: 'CampusLockUp',
    EquipmentRequest: 'EquipmentRequest',
    PlatformChangeRequest: 'PlatformChangeRequest',
    LiveServicesTechRequest: 'LiveServicesTechRequest',
    PerformingArtsTechRequest: 'PerformingArtsTechRequest',
    SaveTheDate: 'SaveTheDate', // New class for Save the Date requests
    JobApplication: 'JobApplication' // Class for job applications
};

// Define Parse SDK classes for Bethel app (Pastor's Tools)
export const BethelAppClasses = {
    User: '_User',
    UserFormRelationship: 'UserFormRelationship',
    Bulletin: 'Bulletin',
    GivingType: 'GivingType',
    Payment: 'Payment',
    PrayerRequest: 'PrayerRequest'
};

// Email-based authentication verification for Pastor's Tools.
//
// This deliberately does NOT go through the Parse SDK. The SDK is a global
// singleton, and pointing it at the Bethel server means every other async
// Parse call in the app (the approval-queue badge in the shell, the session
// check at load) briefly reads the wrong server while this one await is in
// flight — an intermittent "not authorized" here, a spurious sign-out there,
// depending purely on timing. A plain fetch touches no shared state, so the
// auth check can't collide with anything.
export const verifyPastoralAccess = async (userEmail) => {
    try {
        const response = await fetch(`${BETHEL_SERVER_URL}/functions/verifyPastoralAccess`, {
            method: 'POST',
            headers: {
                'X-Parse-Application-Id': BETHEL_APP_ID,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: userEmail }),
        });

        if (!response.ok) {
            throw new Error(`Bethel server returned ${response.status}`);
        }

        // Parse cloud functions wrap their return value in { result: ... }.
        const { result } = await response.json();

        return {
            hasAccess: result.hasAccess,
            user: result.user ? {
                email: userEmail, // Keep the email from the original request
                firstName: result.user.firstName,
                lastName: result.user.lastName,
                pastoralTeam: true // If we got here, they have pastoral team access
            } : null
        };
    } catch (err) {
        console.error('Error verifying pastoral access:', err);

        return {
            hasAccess: false,
            user: null,
            error: err.message
        };
    }
};

export default Parse;